import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Input } from "@vv/ui";
import { useEffect, useState } from "react";
import { useRequestsDispatch, useRequestsSelector } from "../state/hooks";
import { addComment, fetchComments } from "../state/requests.thunks";

export interface CommentsSectionProps {
  requestId: string;
}

export function CommentsSection({ requestId }: CommentsSectionProps) {
  const dispatch = useRequestsDispatch();
  const comments = useRequestsSelector((state) => state.requests.comments[requestId]) ?? [];
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    dispatch(fetchComments(requestId));
  }, [dispatch, requestId]);

  async function handleSend() {
    if (!body.trim()) return;
    setSending(true);
    try {
      await dispatch(addComment({ requestId, body: body.trim() })).unwrap();
      setBody("");
    } finally {
      setSending(false);
    }
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Activity
      </Typography>

      {comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No comments yet.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {comments.map((comment) => (
            <Box key={comment.id}>
              <Typography variant="caption" color="text.secondary">
                {new Date(comment.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2">{comment.body}</Typography>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <Input
          label="Add a comment"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          multiline
          minRows={2}
          fullWidth
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void handleSend();
            }
          }}
        />
        <IconButton
          color="primary"
          disabled={sending || body.trim().length === 0}
          onClick={handleSend}
          aria-label="send comment"
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
